import React, { useCallback, useEffect, useState } from "react";
import {
  Table, Tag, Space, Button, Image, Tabs, Modal, Input, Tooltip, message, Popconfirm,
} from "antd";
import dayjs from "dayjs";
import {
  getPublicDealsQueue,
  approvePublicDeal,
  rejectPublicDeal,
  removePublicDeal,
} from "../../../api/publicDeals";

// Moderation queue for community-posted deals from yupup.com's directory
// pages. Nothing a visitor submits is publicly visible until approved here.

const STATUS_TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "removed", label: "Removed" },
  { key: "expired", label: "Expired" },
];

const STATUS_COLORS = {
  pending: "gold",
  approved: "green",
  rejected: "red",
  removed: "default",
  expired: "default",
};

const offerLabel = (d) => {
  if (d.discountPercent) return `${d.discountPercent}% off`;
  const map = { bogo: "BOGO", free: "Freebie", bundle: "Bundle", amount_off: "$ off", percent_off: "% off" };
  return map[d.offerType] || d.offerType;
};

const PublicDealsModeration = () => {
  const [status, setStatus] = useState("pending");
  const [deals, setDeals] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(null); // deal id currently being acted on
  const [rejecting, setRejecting] = useState(null); // deal being rejected (modal)
  const [rejectReason, setRejectReason] = useState("");

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPublicDealsQueue({ status, page, pageSize });
      setDeals(res?.deals || []);
      setTotal(res?.total || 0);
    } finally {
      setLoading(false);
    }
  }, [status, page, pageSize]);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  const act = async (fn, id, okMsg) => {
    setActing(id);
    try {
      const res = await fn(id);
      if (res?.success) {
        message.success(okMsg);
        fetchDeals();
      }
    } finally {
      setActing(null);
    }
  };

  const submitReject = async () => {
    const id = rejecting?._id;
    setActing(id);
    try {
      const res = await rejectPublicDeal(id, rejectReason.trim());
      if (res?.success) {
        message.success("Deal rejected");
        setRejecting(null);
        setRejectReason("");
        fetchDeals();
      }
    } finally {
      setActing(null);
    }
  };

  const columns = [
    {
      title: "Deal",
      dataIndex: "title",
      key: "deal",
      render: (_, d) => (
        <Space>
          <Image
            src={d.image}
            alt={d.title}
            width={72}
            height={44}
            style={{ objectFit: "cover", borderRadius: 6 }}
            placeholder
          />
          <div style={{ maxWidth: 260 }}>
            <div style={{ fontWeight: 600 }}>{d.title}</div>
            <div style={{ fontSize: 12, color: "#888" }} className="line-clamp-2">
              {d.description}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: "Business",
      key: "business",
      render: (_, d) => (
        <div>
          <div>{d.businessRef?.name}</div>
          <Tag color={d.businessRef?.kind === "directory" ? "blue" : "orange"}>
            {d.businessRef?.kind === "directory" ? "In directory" : "Unverified"}
          </Tag>
          {d.businessRef?.key && (
            <div style={{ fontSize: 11, color: "#888" }}>
              <a
                href={`https://yupup.com/directory/${d.businessRef.key}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {d.businessRef.key}
              </a>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Offer",
      key: "offer",
      render: (_, d) => (
        <div>
          <Tag>{offerLabel(d)}</Tag>
          <div style={{ fontSize: 12 }}>
            {d.dealPrice != null && <b>${Number(d.dealPrice).toFixed(2)} </b>}
            {d.originalPrice != null && (
              <span style={{ textDecoration: d.dealPrice != null ? "line-through" : "none", color: "#888" }}>
                ${Number(d.originalPrice).toFixed(2)}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Valid",
      key: "valid",
      render: (_, d) => (
        <div style={{ fontSize: 12 }}>
          {d.validFrom ? dayjs(d.validFrom).format("MMM D") : "now"} →{" "}
          {dayjs(d.validThrough).format("MMM D, YYYY")}
        </div>
      ),
    },
    {
      title: "Submitter",
      key: "submitter",
      render: (_, d) => (
        <div style={{ fontSize: 12 }}>
          <div>{d.submitter?.email}</div>
          <div style={{ color: "#888" }}>{dayjs(d.createdAt).format("MMM D, HH:mm")}</div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => <Tag color={STATUS_COLORS[s] || "default"}>{s}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, d) => (
        <Space>
          {d.status === "pending" && (
            <>
              <Popconfirm
                title="Approve this deal?"
                description="It goes live on yupup.com within ~5 minutes."
                onConfirm={() => act(approvePublicDeal, d._id, "Deal approved — live shortly")}
              >
                <Button type="primary" size="small" loading={acting === d._id}>
                  Approve
                </Button>
              </Popconfirm>
              <Button
                danger
                size="small"
                onClick={() => setRejecting(d)}
                loading={acting === d._id}
              >
                Reject
              </Button>
            </>
          )}
          {d.status === "approved" && (
            <Popconfirm
              title="Take this deal down?"
              onConfirm={() => act(removePublicDeal, d._id, "Deal removed")}
            >
              <Tooltip title="Removes it from yupup.com">
                <Button danger size="small" loading={acting === d._id}>
                  Remove
                </Button>
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Public Deals</h2>
      <p style={{ color: "#888", marginBottom: 12 }}>
        Community-posted deals from yupup.com. Nothing shows publicly until approved here.
      </p>
      <Tabs
        activeKey={status}
        items={STATUS_TABS}
        onChange={(k) => {
          setStatus(k);
          setPage(1);
        }}
      />
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={deals}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        scroll={{ x: 900 }}
      />

      <Modal
        title={`Reject "${rejecting?.title || ""}"`}
        open={Boolean(rejecting)}
        onOk={submitReject}
        okText="Reject deal"
        okButtonProps={{ danger: true, loading: acting === rejecting?._id }}
        onCancel={() => {
          setRejecting(null);
          setRejectReason("");
        }}
      >
        <p style={{ marginBottom: 8, color: "#888" }}>
          Optional reason (kept internally, not emailed to the submitter yet):
        </p>
        <Input.TextArea
          rows={3}
          maxLength={500}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="e.g. Image unusable / deal looks fake / duplicate"
        />
      </Modal>
    </div>
  );
};

export default PublicDealsModeration;
